import { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CalendarDays, Briefcase, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import SplashScreen from "../components/SplashScreen";
//now this page is disabled
const FREELANCE_ENABLED = false;

const CARDS = [
  {
    key: "events",
    title: "EarnMate events",
    blurb: "Find event work, or hire & manage staff for your event",
    icon: CalendarDays,
    info: "For event managers hiring staff, and workers looking for event-day gigs like weddings, catering, and functions.",
  },
  {
    key: "freelance",
    title: "EarnMate freelance",
    blurb: "Find projects or hire skilled freelancers",
    icon: Briefcase,
    info: "For clients hiring freelance skill-based work, and freelancers offering their services.",
  },
];

const VISIBLE_CARDS = CARDS.filter(c => FREELANCE_ENABLED || c.key !== "freelance");

function WorkspacePicker({ mode, onLogin, onSignup, onContinue, continuing }) {
  const [selected, setSelected] = useState(VISIBLE_CARDS.length === 1 ? VISIBLE_CARDS[0].key : null);
  const [error, setError] = useState(false);
  const [infoOpen, setInfoOpen] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!infoOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setInfoOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [infoOpen]);

  const requireSelection = (fn) => () => {
    if (!selected) { setError(true); return; }
    fn(selected);
  };

  return (
    <div className="w-full max-w-md px-6" ref={containerRef}>
      <div className="text-center mb-8">
        <span className="font-display font-black text-4xl tracking-tighter bg-clip-text text-transparent"
          style={{ backgroundImage: "linear-gradient(90deg, #ec4cb8 0%, #c026d3 35%, #a855f7 70%, #7c3aed 100%)" }}>
          EarnMate
        </span>
        <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>Work. Hire. Earn.</p>
      </div>
        <div className="space-y-3 mb-2">
        {VISIBLE_CARDS.map(c => {
          const Icon = c.icon;
          const isSelected = selected === c.key;
          return (
            <div
              key={c.key}
              onClick={() => { setSelected(c.key); setError(false); }}
              className="relative rounded-2xl p-4 flex gap-3 items-start cursor-pointer transition-all"
              style={{
                background: isSelected ? "rgba(124,58,237,0.12)" : "rgba(255,255,255,0.04)",
                border: isSelected ? "1.5px solid #a855f7" : "1px solid rgba(255,255,255,0.08)",
                boxShadow: isSelected ? "0 0 0 3px rgba(168,85,247,0.15)" : "none",
              }}
              data-testid={`workspace-card-${c.key}`}
            >
              {isSelected && VISIBLE_CARDS.length > 1 && (
                <div className="absolute top-2.5 left-2.5 w-4.5 h-4.5 rounded-full grid place-items-center text-[10px] text-white"
                  style={{ background: "linear-gradient(135deg, #ec4cb8, #7c3aed)" }}>✓</div>
              )}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); setInfoOpen(infoOpen === c.key ? null : c.key); }}
                className="absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center transition-colors"
                style={{ color: "rgba(255,255,255,0.4)" }}
                aria-label="What's this?"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
              <Icon className="w-5 h-5 mt-6 shrink-0" style={{ color: "#c084fc" }} />
              <div className="mt-6">
                <p className="font-medium text-sm text-white">{c.title}</p>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>{c.blurb}</p>
              </div>
              {infoOpen === c.key && (
                <div className="absolute z-10 top-9 right-2 w-56 p-3 rounded-xl text-xs leading-relaxed shadow-xl"
                  style={{ background: "#1a1625", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.75)" }}>
                  {c.info}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <p className="text-red-400 text-xs text-center font-medium mt-3 mb-1" data-testid="workspace-select-error">
          Please select Events or Freelance to continue
        </p>
      )}
      <div className={`flex gap-3 ${error ? "mt-2" : "mt-6"}`}>
        {mode === "onboarding" ? (
          <button
            onClick={requireSelection(onContinue)}
            disabled={continuing}
            className="flex-1 rounded-full py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-60"
            style={{ background: "linear-gradient(90deg, #ec4cb8, #7c3aed)" }}
            data-testid="workspace-continue-btn"
          >
            {continuing ? "Saving…" : "Continue"}
          </button>
        ) : (
          <>
            <button
              onClick={requireSelection(onLogin)}
              className="flex-1 rounded-full py-2.5 text-sm font-medium text-white transition-colors"
              style={{ border: "1px solid rgba(255,255,255,0.2)", background: "transparent" }}
              data-testid="workspace-login-btn"
            >
              Log in
            </button>
            <button
              onClick={requireSelection(onSignup)}
              className="flex-1 rounded-full py-2.5 text-sm font-medium text-white"
              style={{ background: "linear-gradient(90deg, #ec4cb8, #7c3aed)" }}
              data-testid="workspace-signup-btn"
            >
              Sign up
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function WorkspaceGate() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [savingChoice, setSavingChoice] = useState(false);

  if (loading) return <SplashScreen />;

  if (!user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center py-10" style={{ background: "#0D0B14" }}>
        <WorkspacePicker
          mode="splash"
          onLogin={(workspace) => nav(`/login?workspace=${workspace}`)}
          onSignup={(workspace) => nav(`/register?workspace=${workspace}`)}
          continuing={false}
        />
      </div>
    );
  }

  if (user.last_workspace === "events" || user.last_workspace === "freelance") {
    return <Navigate to={`/${user.last_workspace}`} replace />;
  }

  const chooseWorkspace = async (workspace) => {
    setSavingChoice(true);
    try {
      await api.patch("/users/me", { last_workspace: workspace });
      await refresh();
      nav(`/${workspace}`, { replace: true });
    } finally {
      setSavingChoice(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center py-10" style={{ background: "#0D0B14" }}>
      <WorkspacePicker mode="onboarding" onContinue={chooseWorkspace} continuing={savingChoice} />
    </div>
  );
}